/**
 * PURPOSE: Ayudas de captura de marcas de Atletismo (tiempos, distancias, alturas, intentos y viento).
 * RESPONSIBILITIES: Limpiar lo que se teclea, expandir la captura rápida sin separadores y validar
 *   la FORMA del texto antes de enviarlo. Puro.
 *
 *   EL CLIENTE ENVÍA TEXTO. El contrato pide `"10.45"`, `"1:58.32"`, `"35:12.40"` para tiempos y
 *   metros (`"7.45"`, `"1.85"`) para distancias y alturas. Aquí solo se normaliza la escritura (coma
 *   decimal, mayúsculas, dígitos corridos); el servidor vuelve a validar y es quien convierte a
 *   `markValue`, ordena y puntúa.
 *
 *   CAPTURA RÁPIDA. Con 3 o más dígitos corridos, los dos últimos son centésimas (o centímetros):
 *   "1045" → "10.45", "15832" → "1:58.32", "351240" → "35:12.40", "745" → "7.45". El valor
 *   normalizado se ve en la celda al salir de ella, así que el operador siempre lo confirma.
 * COLLABORATORS: utils/athleticsRoundDraft.js, grids de area/AtletismoResultadosArea.
 * ROUTE: winscore/src/utils/athleticsMarks.js
 */

/** Estados de fila que se pueden teclear directamente en la celda de marca de una carrera. */
export const TRACK_STATUS_CODES = Object.freeze(["DNS", "DNF", "DQ"]);
/**
 * Estados MANUALES de saltos y lanzamientos (SHL) y de salto alto/garrocha (SVA). `OK`, `NM` y `NH`
 * los calcula el servidor a partir de los intentos: en el borrador valen "automático" (null).
 */
export const FIELD_STATUS_CODES = Object.freeze(["DNS", "DQ"]);
export const VERTICAL_STATUS_CODES = FIELD_STATUS_CODES;
/** Estados que el servidor acepta como manuales en cualquier formato. */
export const MANUAL_STATUS_CODES = Object.freeze(["DNS", "DNF", "DQ"]);

const asText = (value) => (value === null || value === undefined ? "" : String(value));

// --------------------------------------------- tiempos ---------------------------------------------

/** Solo dígitos, un punto (la coma cuenta como punto) y hasta dos ":" antes del punto. */
export const sanitizeTimeTyping = (raw) => {
  let out = "";
  let seenDot = false;
  let colons = 0;
  for (const char of asText(raw).replace(/,/g, ".")) {
    if (char >= "0" && char <= "9") out += char;
    else if (char === "." && !seenDot) {
      seenDot = true;
      out += char;
    } else if (char === ":" && !seenDot && colons < 2) {
      colons += 1;
      out += char;
    }
  }
  return out;
};

/** "1045" → "10.45" · "15832" → "1:58.32" · "351240" → "35:12.40" · "1053012" → "1:05:30.12". */
export const expandTimeDigits = (digits) => {
  const value = asText(digits);
  if (!/^\d{3,8}$/.test(value)) return value;
  const cents = value.slice(-2);
  let rest = value.slice(0, -2);
  const seconds = rest.slice(-2);
  rest = rest.slice(0, -2);
  if (!rest) return `${Number(seconds)}.${cents}`;
  const minutes = rest.slice(-2);
  rest = rest.slice(0, -2);
  if (!rest) return `${Number(minutes)}:${seconds}.${cents}`;
  return `${Number(rest)}:${minutes}:${seconds}.${cents}`;
};

export const normalizeTimeMark = (raw) => {
  const clean = sanitizeTimeTyping(asText(raw).trim());
  if (/^\d{3,8}$/.test(clean)) return expandTimeDigits(clean);
  return clean.replace(/\.$/, "");
};

/** Forma de un tiempo: `ss(.ccc)`, `m:ss(.ccc)` o `h:mm:ss(.ccc)`; los segmentos tras ":" < 60. Vacío = sin marca. */
export const isValidTimeMark = (text) => {
  const value = asText(text).trim();
  if (!value) return true;
  const match = /^(\d{1,2})((?::\d{2}){0,2})(?:\.(\d{1,3}))?$/.exec(value);
  if (!match) return false;
  const segments = match[2] ? match[2].slice(1).split(":") : [];
  return segments.every((segment) => Number(segment) < 60);
};

// ------------------------------------------- distancias -------------------------------------------

export const sanitizeDistanceTyping = (raw) => {
  let out = "";
  let seenDot = false;
  for (const char of asText(raw).replace(/,/g, ".")) {
    if (char >= "0" && char <= "9") out += char;
    else if (char === "." && !seenDot) {
      seenDot = true;
      out += char;
    }
  }
  return out;
};

/** "745" → "7.45" · "6512" → "65.12" · "7.4" → "7.40" · "7" → "7". */
export const normalizeDistanceMark = (raw) => {
  const clean = sanitizeDistanceTyping(asText(raw).trim()).replace(/\.$/, "");
  if (/^\d{3,5}$/.test(clean)) return `${Number(clean.slice(0, -2))}.${clean.slice(-2)}`;
  if (/^\d+\.\d$/.test(clean)) return `${clean}0`;
  return clean;
};

export const isValidDistanceMark = (text) => {
  const value = asText(text).trim();
  if (!value) return true;
  return /^\d{1,3}(?:\.\d{1,2})?$/.test(value);
};

// -------------------------------------- intentos (SHL) --------------------------------------

/** Lo que queda en la celda mientras se teclea: "X" (nulo), "-" (pasa) o una distancia. */
export const sanitizeAttemptTyping = (raw) => {
  const value = asText(raw).trim().toUpperCase();
  if (!value) return "";
  if (value.startsWith("X")) return "X";
  if (value.startsWith("-")) return "-";
  return sanitizeDistanceTyping(value);
};

export const normalizeAttemptResult = (raw) => {
  const value = sanitizeAttemptTyping(raw);
  if (value === "X" || value === "-") return value;
  return normalizeDistanceMark(value);
};

export const isValidAttemptResult = (text) => {
  const value = asText(text).trim().toUpperCase();
  if (!value || value === "X" || value === "-") return true;
  return isValidDistanceMark(value);
};

// --------------------------------------- alturas (SVA) ---------------------------------------

/** Secuencia de O/X/- en mayúsculas (el cero cuenta como O), máximo 3 intentos por altura. */
/** Intentos por altura en salto alto y garrocha cuando la prueba no lo configura. */
export const DEFAULT_ATTEMPTS_PER_HEIGHT = 3;

export const sanitizeHeightAttempts = (raw, maxAttempts = DEFAULT_ATTEMPTS_PER_HEIGHT) =>
  asText(raw)
    .toUpperCase()
    .replace(/0/g, "O")
    .replace(/[^OX-]/g, "")
    .slice(0, maxAttempts);

/**
 * Marcación libre: cualquier combinación de O (válido), X (nulo) y - (pasa) de hasta `maxAttempts` intentos, sin
 * validar el orden (el servidor cuenta hasta el primer O de cada altura). Vacío = sin intento.
 */
export const isValidHeightAttempts = (text, maxAttempts = DEFAULT_ATTEMPTS_PER_HEIGHT) => {
  const value = asText(text).trim().toUpperCase();
  if (!value) return true;
  return /^[OX-]+$/.test(value) && value.length <= maxAttempts;
};

/** 130 → "1.30". */
export const formatHeightCm = (cm) => {
  const value = Number(cm);
  if (!Number.isFinite(value)) return "";
  return (value / 100).toFixed(2);
};

/** "1.35" o "1,35" → 135 · "135" → 135 · "2" → 200. Devuelve null si no es una altura legible. */
export const parseHeightInput = (raw) => {
  const value = asText(raw).trim().replace(/,/g, ".");
  if (!value) return null;
  if (!/^\d+(?:\.\d{1,2})?$/.test(value)) return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  if (value.includes(".") || number < 10) return Math.round(number * 100);
  return Math.round(number);
};

// ----------------------------------------------- viento -----------------------------------------------

export const sanitizeWindTyping = (raw) => {
  const value = asText(raw).replace(/,/g, ".");
  let out = "";
  let seenDot = false;
  for (const char of value) {
    if ((char === "+" || char === "-") && out === "") out += char;
    else if (char >= "0" && char <= "9") out += char;
    else if (char === "." && !seenDot) {
      seenDot = true;
      out += char;
    }
  }
  return out;
};

/** "1.2" → "+1.2" · "-0.5" → "-0.5" · "0" → "0.0" · "" → null. Lo que no se entiende se deja tal cual. */
export const normalizeWind = (raw) => {
  const value = sanitizeWindTyping(asText(raw).trim()).replace(/\.$/, "");
  if (!value || value === "+" || value === "-") return null;
  if (!/^[+-]?\d{1,2}(?:\.\d)?$/.test(value)) return value;
  const number = Number(value);
  if (number === 0) return "0.0";
  return number > 0 ? `+${number.toFixed(1)}` : number.toFixed(1);
};

export const isValidWind = (text) => {
  const value = asText(text).trim();
  if (!value) return true;
  return /^[+-]?\d{1,2}(?:\.\d)?$/.test(value);
};

// ------------------------------------- celda de marca de carrera -------------------------------------

/**
 * Lo tecleado en la celda de marca de una carrera: si son letras de un estado (DNS, DNF, DQ) se
 * interpretan como estado; si no, como tiempo. Mientras se escribe un prefijo ("D", "DN") se deja
 * en la celda sin convertir.
 * @returns {{ mark: string, status?: string, pending?: boolean }}
 */
export const interpretTrackMarkTyping = (raw, statusCodes = TRACK_STATUS_CODES) => {
  const upper = asText(raw).trim().toUpperCase();
  if (statusCodes.includes(upper)) return { mark: "", status: upper };
  if (/^[A-Z]+$/.test(upper) && statusCodes.some((code) => code.startsWith(upper))) {
    return { mark: upper, pending: true };
  }
  return { mark: sanitizeTimeTyping(raw) };
};

/** Tipo de marca de la fila según el formato de la prueba. */
export const markKindForFormat = (format) => {
  if (format === "SHL") return "distance";
  if (format === "SVA") return "height";
  return "time";
};
